import React, { useMemo } from 'react';
import { MdLocationOn, MdPeople, MdBusiness, MdAttachMoney, MdStar, MdTrendingUp } from 'react-icons/md';
import BaseChart from '../../../Components/Analytics/BaseChart';
import { useChartTheme } from '../../../Context/ThemeContext';
import {
  transformProviderVisitStatus,
  transformProviderAgeRanges,
  transformProviderStateDistribution,
  transformProviderCityDistribution,
  transformProviderCPTMix,
  transformProviderICD10Mix,
  transformProviderFacilities,
  transformProviderScoreRadar,
  transformProviderTrend,
  transformProviderBenchmark,
} from '../../../Utils/AnalyticsTransformers';
import {
  appointmentStatusOption,
  ageDistributionOption,
  providerProductivityGaugeOption,
  providerScoreRadarOption,
  providerTrendOption,
  providerBenchmarkBarOption,
  providerHorizontalBarOption,
  providerRoseOption,
  providerDotPlotOption,
  providerHeatmapStripOption,
  providerCityAreaOption,
} from '../../../Utils/AnalyticsOptions';

export default function AnalyticsProviderCharts({ provider = null, trend = null, benchmark = null, loading = false, error = null }) {
  useChartTheme();
  const hasData = Boolean(provider);

  const visitStatusData = useMemo(() => transformProviderVisitStatus(provider), [provider]);
  const ageData = useMemo(() => transformProviderAgeRanges(provider), [provider]);
  const stateData = useMemo(() => transformProviderStateDistribution(provider), [provider]);
  const cityData = useMemo(() => transformProviderCityDistribution(provider), [provider]);
  const cptData = useMemo(() => transformProviderCPTMix(provider), [provider]);
  const icdData = useMemo(() => transformProviderICD10Mix(provider), [provider]);
  const facilityData = useMemo(() => transformProviderFacilities(provider), [provider]);
  const scoreRadarData = useMemo(() => transformProviderScoreRadar(provider), [provider]);
  const trendData = useMemo(() => transformProviderTrend(trend), [trend]);
  const benchmarkData = useMemo(() => transformProviderBenchmark(benchmark), [benchmark]);

  const metricCards = [
    {
      label: 'Patients',
      value: (provider?.totals?.patient_count ?? 0).toLocaleString(),
      icon: <MdPeople size={22} style={{ color: 'var(--hc-brand-ink)' }} />,
    },
    {
      label: 'Visits',
      value: (provider?.totals?.visit_count ?? 0).toLocaleString(),
      icon: <MdBusiness size={22} style={{ color: 'var(--hc-brand-ink)' }} />,
    },
    {
      label: 'Appointments',
      value: (provider?.totals?.appointment_count ?? 0).toLocaleString(),
      icon: <MdLocationOn size={22} style={{ color: 'var(--hc-brand-ink)' }} />,
    },
    {
      label: 'Revenue',
      value: `$${(provider?.revenue?.total ?? 0).toLocaleString()}`,
      icon: <MdAttachMoney size={22} style={{ color: 'var(--hc-brand-ink)' }} />,
    },
    {
      label: 'Revenue / Visit',
      value: `$${(provider?.revenue?.revenue_per_visit ?? 0).toLocaleString()}`,
      icon: <MdTrendingUp size={22} style={{ color: 'var(--hc-brand-ink)' }} />,
    },
    {
      label: 'Productivity Score',
      value: `${provider?.productivity_score?.score ?? 0}%`,
      icon: <MdStar size={22} style={{ color: 'var(--hc-brand-ink)' }} />,
    },
  ];

  return (
    <div className="row g-4">
      <div className="col-12">
        <div className="d-flex flex-column flex-md-row justify-content-between align-items-start gap-2 mb-4">
          <div>
            <h3 className="fw-bold mb-1" style={{ fontSize: 'clamp(1.2rem, 2vw, 1.6rem)' }}>Provider productivity charts</h3>
            <p className="text-secondary mb-0">Visualize this provider's performance across the selected date range.</p>
          </div>
          <div className="text-muted small">{hasData ? `${provider.facilities?.length || 0} facilities` : 'Waiting for analytics'}</div>
        </div>
      </div>

      <div className="col-12">


        <div className="row g-3">
          {metricCards.map((card) => (
            <div key={card.label} className="col-12 col-sm-6 col-xl-4">
              <div className="hc-surface rounded-4 p-3 h-100 d-flex flex-column justify-content-between">
                <div className="d-flex align-items-start gap-3">
                  <div className="d-inline-flex align-items-center justify-content-center rounded-4 flex-shrink-0" style={{ width: 48, height: 48, backgroundColor: 'rgba(60, 159, 162, 0.14)' }}>
                    {card.icon}
                  </div>
                  <div className="min-w-0">
                    <div className="text-secondary small text-uppercase fw-semibold">{card.label}</div>
                    <div className="fw-bold fs-5 mt-1 text-break">{card.value}</div>
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>

      <div className="col-12 col-xl-6 col-xxl-4">
        <BaseChart
          title="Productivity score"
          subtitle="Composite score for this provider"
          loading={loading}
          noData={!loading && !hasData}
          error={error}
          option={providerProductivityGaugeOption({ value: provider?.productivity_score?.score ?? 0 })}
        />
      </div>

      <div className="col-12 col-xl-6 col-xxl-4">
        <BaseChart
          title="Productivity breakdown"
          subtitle="Normalized components behind the score"
          loading={loading}
          noData={!loading && !hasData}
          error={error}
          option={providerScoreRadarOption({ data: scoreRadarData })}
        />
      </div>

      <div className="col-12 col-xxl-4">
        <BaseChart
          title="Visit status"
          subtitle="Appointment outcomes for this provider"
          loading={loading}
          noData={!loading && !hasData}
          error={error}
          option={appointmentStatusOption({ data: visitStatusData })}
        />
      </div>

      <div className="col-12 col-xl-6">
        <BaseChart
          title="Trend over time"
          subtitle="Patients, visits, and revenue by period"
          loading={loading}
          noData={!loading && !trendData.buckets.length}
          error={error}
          option={providerTrendOption(trendData)}
        />
      </div>

      <div className="col-12 col-xl-6">
        <BaseChart
          title="Provider vs. organization"
          subtitle="How this provider compares to the organization average"
          loading={loading}
          noData={!loading && !benchmarkData.length}
          error={error}
          option={providerBenchmarkBarOption({ data: benchmarkData })}
        />
      </div>

      <div className="col-12 col-xl-6">
        <BaseChart
          title="Facility split"
          subtitle="Visit volume by facility"
          loading={loading}
          noData={!loading && !facilityData.length}
          error={error}
          option={providerDotPlotOption({ data: facilityData, title: 'Facility Split', subtitle: 'Visit volume by facility' })}
        />
      </div>

      <div className="col-12 col-xl-6">
        <BaseChart
          title="Top procedures (CPT)"
          subtitle="Visit volume by CPT code"
          loading={loading}
          noData={!loading && !cptData.length}
          error={error}
          option={providerRoseOption({ data: cptData, title: 'Top Procedures (CPT)', subtitle: 'Visit volume by CPT code' })}
        />
      </div>

      <div className="col-12 col-xl-6">
        <BaseChart
          title="Top diagnoses (ICD-10)"
          subtitle="Visit volume by diagnosis code"
          loading={loading}
          noData={!loading && !icdData.length}
          error={error}
          option={providerHorizontalBarOption({ data: icdData, title: 'Top Diagnoses (ICD-10)', subtitle: 'Visit volume by diagnosis code', multiColor: true })}
        />
      </div>

      <div className="col-12 col-xl-6">
        <BaseChart
          title="Patient age distribution"
          subtitle="Age groups for this provider's patients"
          loading={loading}
          noData={!loading && !hasData}
          error={error}
          option={ageDistributionOption({ data: ageData })}
        />
      </div>

      <div className="col-12 col-xl-6">
        <BaseChart
          title="Patients by state"
          subtitle="Geographic distribution of this provider's patients"
          loading={loading}
          noData={!loading && !stateData.length}
          error={error}
          option={providerHeatmapStripOption({ data: stateData, title: 'Patients by State', subtitle: 'Geographic distribution' })}
        />
      </div>

      <div className="col-12 col-xl-6">
        <BaseChart
          title="Top patient cities"
          subtitle="Most common patient cities"
          loading={loading}
          noData={!loading && !cityData.length}
          error={error}
          option={providerCityAreaOption({ data: cityData, title: 'Top Patient Cities', subtitle: 'Most common patient cities' })}
        />
      </div>
    </div>
  );
}
